import { useState } from "react";
import { SkewLoader } from "react-spinners";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import { enumToStringConverter } from "../../../../helpers/appHelpers";
import { WorkTimeUnit } from "../../../../models/employee-work-time.dto";
import { TimeRequestStatus } from "../../../../models/time-request.dto";
import useUserStore from "../../../../state-management/useUserStore";
import {
	approvalProgress,
	formatTime,
	StatusBadge,
	TIME_REQUEST_LABELS,
	TimeRequestKind,
	TimeRequestListMode,
} from "../data";
import { useTimeRequestDetail } from "../useTimeRequestApi";
import RevokeTimeRequestModal from "./RevokeTimeRequestModal";
import TimeRequestResponseModal from "./TimeRequestResponseModal";

type TimeRequestDetailsModalProps = {
	kind: TimeRequestKind;
	mode: TimeRequestListMode;
	requestId: number;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onChanged: () => void;
};

const formatDate = (value?: string | null) =>
	value
		? new Date(value).toLocaleDateString("en-US", {
			month: "short", day: "numeric", year: "numeric",
		})
		: "--";

const Field = ({ title, children }: { title: string, children: React.ReactNode }) => (
	<div>
		<h2 className="text-gray-500 text-2xs">{title}</h2>
		<div className="text-2sm">{children}</div>
	</div>
);

const TimeRequestDetailsModal = ({
	kind,
	mode,
	requestId,
	open,
	setOpen,
	onChanged,
}: TimeRequestDetailsModalProps) => {
	const { currentUser, userType } = useUserStore();
	const companyProfile = (currentUser.user.companyProfiles || [])[0];
	const [openResponseModal, setOpenResponseModal] = useState(false);
	const [openRevokeModal, setOpenRevokeModal] = useState(false);
	const { data, isLoading } = useTimeRequestDetail(kind, requestId);
	const request = data?.data;

	const responses: any[] = (kind === "workTime"
		? request?.employeeWorkTimeResponses
		: request?.employeeOvertimeEntryResponses) || [];
	const respondedByMe = responses.some(
		(response) => response.approvingEmployeeId === companyProfile?.employeeId,
	);
	// HR revoke from the company list; approvers from their own responses
	const isAdmin = userType === "HR" || userType === "ORGANIZATION";
	const overtimeEntry = request?.employeeOvertimeEntry;
	// Only approvals that gave payroll an entry, or still will, can be revoked
	const hasPayrollEntry = kind === "workTime"
		? !!request?.employeeWorkTime
		: !!overtimeEntry || !!request?.awaitingPayrollEntry;
	const canRevoke = request?.status === TimeRequestStatus.APPROVED
		&& hasPayrollEntry
		&& ((mode === "ALL" && isAdmin) || respondedByMe);

	return (<>
		<ModalComponent
			open={open}
			title={`${TIME_REQUEST_LABELS[kind].name} Details`}
			width={680}
			handleClose={() => setOpen(false)}
			additionalStyles={{ opacity: openResponseModal || openRevokeModal ? 0 : 100 }}
		>
			{isLoading ? (
				<div className="text-center py-10">
					<SkewLoader color="#F58B00" />
				</div>
			) : !request ? (
				<p className="text-center text-2sm text-gray-500 py-10">
					This request could not be found, or you don't have access to it.
				</p>
			) : (
				<div className="text-charcoal space-y-6 mt-3">
					<section className="flex items-start justify-between">
						<Field title="Status"><StatusBadge status={request.status} /></Field>
						<Field title="Approval">{approvalProgress(request)}</Field>
					</section>
					<section className="grid grid-cols-2 gap-6">
						<Field title="Employee">
							{`${request.employee?.firstName || ""} ${request.employee?.lastName || ""}`}
						</Field>
						<Field title="Pay Period">{request.payPeriod?.code || "--"}</Field>
						{kind === "workTime" ? (
							<Field title="Time Worked">
								{formatTime(request.timeValue, request.timeUnit)}
							</Field>
						) : (<>
							<Field title="Overtime">{request.overtime?.name || "--"}</Field>
							<Field title="Hours">
								{formatTime(request.numberOfHours, WorkTimeUnit.HOUR)}
							</Field>
						</>)}
						<Field title="Date Submitted">{formatDate(request.createdAt)}</Field>
					</section>

					{kind === "overtime" && request.status === TimeRequestStatus.APPROVED
						&& (overtimeEntry || request.awaitingPayrollEntry) && (
						<p className="text-2sm bg-secondary-gray rounded-lg p-4">
							{!overtimeEntry
								? "Payroll had already run for this pay period. This overtime "
									+ "will be paid once the next pay period opens."
								: overtimeEntry.payPeriodId !== request.payPeriodId
									? `Payroll had already run for ${request.payPeriod?.code}, so this `
										+ `overtime is paid in ${overtimeEntry.payPeriod?.code}.`
									: "This overtime is included in payroll for its pay period."}
						</p>
					)}
					{kind === "workTime" && request.payrollRerunRequired && (
						<p className="text-2sm bg-yellow-50 text-yellow-800 rounded-lg p-4">
							Payroll for {request.payPeriod?.code} was run before this was
							approved. Run payroll again to include it.
						</p>
					)}
					{request.status === TimeRequestStatus.REVOKED && (
						<Field title="Revocation Reason">{request.revocationReason || "--"}</Field>
					)}

					<section>
						<h2 className="text-gray-500 text-2xs mb-2">Responses</h2>
						{responses.length === 0 ? (
							<p className="text-2sm">No responses yet</p>
						) : (
							<ul className="space-y-3">
								{responses.map((response) => (
									<li key={response.id} className="border-b border-gray-100 pb-3">
										<div className="flex justify-between text-2sm">
											<span>
												Level {response.approverLevel}:{" "}
												{response.employee
													? `${response.employee.firstName} ${response.employee.lastName}`
													: "Approver"}
											</span>
											<span>
												{enumToStringConverter(response.responseType)} on{" "}
												{formatDate(response.createdAt)}
											</span>
										</div>
										{response.comment && (
											<p className="text-2xs text-gray-600 mt-1">{response.comment}</p>
										)}
									</li>
								))}
							</ul>
						)}
					</section>

					{(request.canRespond || canRevoke) && (
						<div className="w-full flex justify-end gap-4 pt-2">
							{canRevoke && (
								<button
									className="h-14 px-8 rounded-lg text-sm border border-secondary-red text-secondary-red"
									onClick={() => setOpenRevokeModal(true)}
								>
									Revoke
								</button>
							)}
							{request.canRespond && (
								<OrangeButton
									title="Respond To Request"
									className="h-14 px-8 rounded-lg text-sm"
									onClick={() => setOpenResponseModal(true)}
								/>
							)}
						</div>
					)}
				</div>
			)}
		</ModalComponent>

		{openResponseModal && (
			<TimeRequestResponseModal
				kind={kind}
				request={request}
				open={openResponseModal}
				setOpen={setOpenResponseModal}
				onResponded={onChanged}
			/>
		)}
		{openRevokeModal && (
			<RevokeTimeRequestModal
				kind={kind}
				request={request}
				open={openRevokeModal}
				setOpen={setOpenRevokeModal}
				onRevoked={onChanged}
			/>
		)}
	</>);
};

export default TimeRequestDetailsModal;
